import { useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const useAssessment = ({ weightRef, streamChat, streaming, onAssessStart }) => {
  const [assessment, setAssessment] = useState(null);
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [flyTarget, setFlyTarget] = useState(null);
  const [locating, setLocating] = useState(false);

  const assess = useCallback(async (lat, lng, placeName) => {
    setSelectedPoint({ lat, lng });
    if (onAssessStart) onAssessStart();
    try {
      const r = await axios.post(`${API}/assess`, { lat, lng, weight_class: weightRef.current, place_name: placeName });
      setAssessment(r.data);
      const label = placeName || `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
      streamChat(`Can I fly my drone at ${label}?`, r.data.context);
    } catch {
      toast.error("Assessment failed — please try again");
    }
  }, [weightRef, streamChat, onAssessStart]);

  const onMapClick = useCallback((lat, lng) => {
    if (!streaming) assess(lat, lng);
  }, [assess, streaming]);

  const onSearchSelect = useCallback((lat, lng, name) => {
    setFlyTarget({ lat, lng, zoom: 12 });
    if (!streaming) assess(lat, lng, name);
  }, [assess, streaming]);

  const onSpotSelect = useCallback((s) => {
    setFlyTarget({ lat: s.lat, lng: s.lng, zoom: 13 });
    if (!streaming) assess(s.lat, s.lng, `${s.name}, ${s.region} (popular flying spot)`);
  }, [assess, streaming]);

  const onDroneDrop = useCallback((lat, lng) => {
    setFlyTarget({ lat, lng, zoom: 15 });
    if (!streaming) assess(lat, lng, "drone drop point");
  }, [assess, streaming]);

  const onLocate = useCallback(() => {
    if (!navigator.geolocation) {
      toast.error("Geolocation not supported by this browser");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocating(false);
        setFlyTarget({ lat: pos.coords.latitude, lng: pos.coords.longitude, zoom: 13 });
        if (!streaming) assess(pos.coords.latitude, pos.coords.longitude, "my current location");
      },
      () => {
        setLocating(false);
        toast.error("Location access denied");
      }
    );
  }, [assess, streaming]);

  return { assessment, selectedPoint, flyTarget, setFlyTarget, locating, onMapClick, onSearchSelect, onSpotSelect, onLocate, onDroneDrop };
};
